<template>
	<div class="dg-range" ref="root">
		<button type="button" class="dg-range-trigger" @click="toggleOpen">
			<i class="far fa-calendar-alt"></i>
			<span>{{ selectedLabel }}</span>
			<i class="fas fa-chevron-down dg-caret" :class="{ 'is-open': isOpen }"></i>
		</button>

		<transition name="dg-popover">
			<div v-if="isOpen" class="dg-range-popover">
				<div class="dg-range-head">
					<button type="button" class="dg-nav" :disabled="!canPrevYear" @click="changeYear(-1)">‹</button>
					<strong class="dg-year">{{ cursorYear }}</strong>
					<button type="button" class="dg-nav" :disabled="!canNextYear" @click="changeYear(1)">›</button>
				</div>

				<div v-if="hasQuickPresets" class="dg-quick-range">
					<button
					v-for="preset in quickPresets"
					:key="preset.key || `${preset.mode || 'past'}-${preset.months}`"
					type="button"
					class="dg-quick-btn"
					:class="{ 'is-active': isPresetActive(preset) }"
					@click="applyPreset(preset)"
				>
						{{ preset.label }}
					</button>
				</div>

				<div v-if="hasQuickPresets" class="dg-range-divider"></div>

				<div class="dg-range-grid">
					<button
						v-for="month in monthsOfYear"
						:key="month.key"
						type="button"
						class="dg-month"
						:class="monthClass(month.key)"
						:disabled="!isSelectable(month.key)"
						@click="selectMonth(month.key)"
					>
						{{ month.label }}
					</button>
				</div>

				<div v-if="showFooter" class="dg-range-foot">
					<span class="dg-step-tag">{{ selectingEnd ? $t('final_date') : $t('initial_date') }}</span>
					<small>{{ $t('relatorio_periodo') }}: {{ selectedLabel }}</small>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
const MONTH_KEYS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function parseMes(mes) {
	const parts = String(mes || '').split('-');

	if (parts.length !== 2) return null;

	const year = Number(parts[0]);
	const month = Number(parts[1]);

	if (!year || month < 1 || month > 12) return null;

	return { year, month };
}

function mesToYear(mes) {
	const parsed = parseMes(mes);
	return parsed ? parsed.year : null;
}

function orderRange(start, end) {
	const ordered = [start, end].sort((a, b) => a.localeCompare(b));
	return { start: ordered[0], end: ordered[1] };
}

function rangeMonths(start, end) {
	const from = parseMes(start);
	const to = parseMes(end);
	
	if (!from || !to) return 0;

	return (to.year - from.year) * 12 + (to.month - from.month) + 1;
}

function limitRangeByAnchor(anchor, target, maxMonths) {
	if (!anchor || !target) return orderRange(anchor, target);

	const ordered = orderRange(anchor, target);

	if (rangeMonths(ordered.start, ordered.end) <= maxMonths) return ordered;

	if (target >= anchor) {
		return orderRange(anchor, shiftMes(anchor, maxMonths - 1));
	}

	return orderRange(shiftMes(anchor, -(maxMonths - 1)), anchor);
}

function shiftMes(mes, delta) {
	const parsed = parseMes(mes);

	if (!parsed) return '';

	const date = new Date(parsed.year, parsed.month - 1 + delta, 1);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');

	return `${year}-${month}`;
}

function getCurrentMesKey() {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	
	return `${year}-${month}`;
}

export default {
	name: 'MonthRangePicker',
	props: {
		value: { type: Object, default: () => ({ start: '', end: '' }) },
		min: { type: String, default: '' },
		max: { type: String, default: '' },
		maxMonths: { type: Number, default: 12 },
		showFooter: { type: Boolean, default: true },
		quickPresets: { type: Array, default: () => [] },
	},
	data() {
		return {
			isOpen: false,
			selectingEnd: false,
			anchorMes: '',
			cursorYear: mesToYear(this.value.end || this.value.start || this.max || this.min) || new Date().getFullYear(),
		};
	},
	computed: {
		minYear() {
			return mesToYear(this.min);
		},
		maxYear() {
			return mesToYear(this.max);
		},
		normalizedRange() {
			const start = this.value.start || '';
			const end = this.value.end || start;

			if (!start || !end) return { start: '', end: '' };

			return orderRange(start, end);
		},
		selectedLabel() {
			const start = this.formatMes(this.normalizedRange.start);
			const end = this.formatMes(this.normalizedRange.end);

			if (start && end && start !== end) return `${start} a ${end}`;
			
			return start || end || '-';
		},
		monthsOfYear() {
			return MONTH_KEYS.map((monthKey, index) => {
				const month = String(index + 1).padStart(2, '0');
				return {
					label: this.$t(`month_short_${monthKey}`),
					key: `${this.cursorYear}-${month}`,
				};
			});
		},
		canPrevYear() {
			if (!this.minYear || !this.maxYear) return false;
			return this.cursorYear > this.minYear;
		},
		canNextYear() {
			if (!this.minYear || !this.maxYear) return false;
			return this.cursorYear < this.maxYear;
		},
		hasQuickPresets() {
			return this.quickPresets.length > 0;
		},
	},
	mounted() {
		document.addEventListener('click', this.onOutsideClick);
		document.addEventListener('keydown', this.onKeydown);
	},
	beforeDestroy() {
		document.removeEventListener('click', this.onOutsideClick);
		document.removeEventListener('keydown', this.onKeydown);
	},
	methods: {
		syncCursorYear() {
			const targetYear = mesToYear(this.value.end || this.value.start || this.max || this.min);
			if (targetYear) this.cursorYear = targetYear;
		},
		openCalendar() {
			this.isOpen = true;
			this.selectingEnd = false;
			this.anchorMes = '';
			this.syncCursorYear();
		},
		closeCalendar() {
			this.isOpen = false;
			this.selectingEnd = false;
			this.anchorMes = '';
		},
		formatMes(mesKey) {
			const parsed = parseMes(mesKey);

			if (!parsed) return '';

			const year = String(parsed.year).slice(2);
			const monthIndex = parsed.month - 1;

			if (monthIndex < 0 || monthIndex > 11) return '';

			const monthKey = MONTH_KEYS[monthIndex];
			return `${this.$t(`month_short_${monthKey}`)}/${year}`;
		},
		toggleOpen() {
			if (!this.min || !this.max) return;
			if (this.isOpen) {
				this.closeCalendar();
				return;
			}
			this.openCalendar();
		},
		onOutsideClick(event) {
			if (!this.isOpen) return;
			if (this.$refs.root && this.$refs.root.contains(event.target)) return;

			this.closeCalendar();
		},
		onKeydown(event) {
			if (!this.isOpen) return;
			if (event.key !== 'Escape') return;
			this.closeCalendar();
		},
		changeYear(direction) {
			if (!this.minYear || !this.maxYear) return;
			const next = this.cursorYear + direction;

			if (this.minYear && next < this.minYear) return;
			if (this.maxYear && next > this.maxYear) return;

			this.cursorYear = next;
		},
		isSelectable(mes) {
			return this.min && this.max && mes >= this.min && mes <= this.max;
		},
		selectMonth(mes) {
			if (!this.isSelectable(mes)) return;

			if (!this.selectingEnd) {
				this.anchorMes = mes;
				this.$emit('input', { start: mes, end: mes });
				this.selectingEnd = true;
				return;
			}

			const base = this.anchorMes || this.value.start || mes;
			const selectedRange = orderRange(base, mes);
			const rangeCount = rangeMonths(selectedRange.start, selectedRange.end);
			const exceededLimit = rangeCount > this.maxMonths;
			let nextRange = limitRangeByAnchor(base, mes, this.maxMonths);
			if (this.min && nextRange.start < this.min) nextRange = { ...nextRange, start: this.min };
			if (this.max && nextRange.end > this.max) nextRange = { ...nextRange, end: this.max };
			nextRange = orderRange(nextRange.start, nextRange.end);
			this.$emit('input', nextRange);
			if (exceededLimit) {
				this.$emit('limit-exceeded', { maxMonths: this.maxMonths, selectedMonths: rangeCount });
			}
			this.closeCalendar();
		},
		applyPreset(preset) {
			if (!preset || !preset.months) return;
			let range = this.buildPastRange(preset.months);
			if (preset.mode === 'future') range = this.buildFutureRange(preset.months);
			if (!range.start || !range.end) return;
			this.$emit('input', range);
			this.closeCalendar();
		},
		getPresetRange(preset) {
			if (!preset || !preset.months) return { start: '', end: '' };
			if (preset.mode === 'future') return this.buildFutureRange(preset.months);
			return this.buildPastRange(preset.months);
		},
		isPresetActive(preset) {
			const presetRange = this.getPresetRange(preset);
			const selectedRange = this.normalizedRange;
			return presetRange.start === selectedRange.start && presetRange.end === selectedRange.end;
		},
		buildPastRange(months) {
			const currentMes = getCurrentMesKey();
			const maxMes = this.max || currentMes;
			const end = maxMes < currentMes ? maxMes : currentMes;
			if (!end) return { start: '', end: '' };

			let start = shiftMes(end, -(months - 1));
			if (this.min && start < this.min) start = this.min;
			return orderRange(start, end);
		},
		buildFutureRange(months) {
			if (!this.min || !this.max) return { start: '', end: '' };
			const currentMes = getCurrentMesKey();
			const start = this.min > currentMes ? this.min : currentMes;
			let end = shiftMes(start, months - 1);
			if (end > this.max) end = this.max;

			const normalized = orderRange(start, end);
			if (rangeMonths(normalized.start, normalized.end) < 1) return { start: '', end: '' };
			return normalized;
		},
		monthClass(mes) {
			const { start, end } = this.normalizedRange;
			const isInRange = this.isSelectable(mes) && start && end && mes >= start && mes <= end;

			return {
				'is-start': mes === start,
				'is-end': mes === end,
				'is-in-range': isInRange,
			};
		},
	},
};
</script>

<style scoped lang="less">
.dg-range {
	position: relative;
}

.dg-range-trigger {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	justify-content: space-between;
	width: 100%;
	appearance: none;
	background: #ffffff;
	border: 1px solid #e7e9f0;
	border-radius: 9px;
	padding: 9px 12px;
	font-size: 13px;
	font-weight: 600;
	color: #1a2036;
	outline: none;
	font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
	text-align: left;
	cursor: pointer;
	transition: border-color 0.16s ease, box-shadow 0.16s ease;
}

.dg-range-trigger:hover {
	border-color: #d2d8e5;
}

.dg-range-trigger:focus,
.dg-range-trigger:focus-visible {
	border-color: rgba(54, 84, 190, 0.7);
	box-shadow: 0 0 0 2px rgba(54, 84, 190, 0.16);
}

.dg-caret {
	font-size: 11px;
	color: #6b7280;
	transition: transform 0.16s ease;
}

.dg-caret.is-open {
	transform: rotate(180deg);
}

.dg-range-popover {
	position: absolute;
	top: calc(100% + 10px);
	right: 0;
	z-index: 30;
	background: rgba(255, 255, 255, 0.98);
	border: 1px solid #e8ebf3;
	border-radius: 14px;
	box-shadow: 0 20px 44px rgba(20, 29, 56, 0.18), 0 2px 6px rgba(20, 29, 56, 0.08);
	backdrop-filter: blur(4px);
	padding: 14px;
	width: min(340px, calc(100vw - 32px));
	overflow: hidden;
	transform-origin: top right;
}

.dg-popover-enter-active {
	animation: popover-in 0.16s ease-out;
}

.dg-popover-leave-active {
	animation: popover-out 0.14s ease-in;
}

.dg-range-popover::before {
	content: '';
	position: absolute;
	inset: 0 0 auto 0;
	height: 3px;
	background: linear-gradient(90deg, #2c2c66 0%, #1b1b3d 45%, #151532 100%);
}

.dg-range-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;
}

.dg-year {
	font-size: 14px;
	color: #111827;
	letter-spacing: 0.2px;
}

.dg-nav {
	border: 1px solid #e7e9f0;
	background: #fff;
	border-radius: 9px;
	width: 28px;
	height: 28px;
	font-size: 16px;
	cursor: pointer;
	color: #374151;
	transition: border-color 0.16s ease, background-color 0.16s ease;
}

.dg-nav:hover:not(:disabled) {
	border-color: #d2d8e5;
	background: #f8fafc;
}

.dg-nav:disabled {
	opacity: 0.35;
	cursor: not-allowed;
}

.dg-range-grid {
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 7px;
}

.dg-quick-range {
	display: flex;
	gap: 8px;
	margin-bottom: 10px;
	flex-wrap: nowrap;
}

.dg-range-divider {
	height: 1px;
	background: #e7e9f0;
	margin: 0 0 10px;
}

.dg-quick-btn {
	border: 1px solid #202049cc;
    background: #202049cc;
	color: white;
    border-radius: 999px;
    padding: 4px 9px;
    width: stretch;
    font-size: 10px;
    font-weight: 700;
    cursor: pointer;
	transition: all 0.16s ease;
	white-space: nowrap;
	line-height: 1.1;
	outline: none;
}

.dg-quick-btn:hover {
	border: 1px solid #202049cc;
    color: #202049cc;
	background: #eef0f8;
}

.dg-quick-btn:active {
	transform: translateY(1px);
}

.dg-quick-btn:focus-visible {
	box-shadow: 0 0 0 2px rgba(27, 27, 61, 0.22);
}

.dg-quick-btn.is-active {
	background: #eef0f8;
	color: #1b1b3d;
	border-color: #1b1b3d;
}

.dg-month {
	border: 1px solid #e7e9f0;
	background: #fff;
	border-radius: 9px;
	padding: 8px 4px;
	font-size: 12px;
	font-weight: 700;
	color: #374151;
	text-transform: uppercase;
	cursor: pointer;
	transition: border-color 0.16s ease, background-color 0.16s ease, color 0.16s ease;
	outline: none;
}

.dg-month:hover:not(:disabled):not(.is-start):not(.is-end) {
	border-color: #9fa4c8;
	background: #f3f4fb;
}

.dg-month.is-in-range {
	background: #eef0f8;
	border-color: #c8cce6;
	color: #1b1b3d;
}

.dg-month.is-start,
.dg-month.is-end {
	background: linear-gradient(150deg, #2c2c66 0%, #1b1b3d 100%);
	border-color: #1b1b3d;
	color: #fff;
	box-shadow: 0 2px 8px rgba(27, 27, 61, 0.32);
}

.dg-month:active:not(:disabled) {
	transform: translateY(1px);
}

.dg-month:focus-visible {
	box-shadow: 0 0 0 2px rgba(27, 27, 61, 0.2);
	border-color: #1b1b3d;
}

.dg-month:disabled {
	opacity: 0.25;
	cursor: not-allowed;
}

.dg-range-foot {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 12px;
	font-size: 11px;
	color: #6b7280;
	font-weight: 700;
}

.dg-step-tag {
	text-transform: uppercase;
	background: #f4f6fb;
	border: 1px solid #e7e9f0;
	padding: 2px 8px;
	border-radius: 999px;
}

.dg-range-foot small {
	text-transform: none;
	font-weight: 600;
}

@keyframes popover-in {
	from {
		opacity: 0;
		transform: translateY(-4px) scale(0.98);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

@keyframes popover-out {
	from {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
	to {
		opacity: 0;
		transform: translateY(-4px) scale(0.98);
	}
}
</style>
